import { useState, useEffect } from 'react';
import { formatLamports, formatTokenAmount } from '../../helpers/redeemFormatUtils';
import { formatTimestamp, parseGameTime } from '../../helpers/redeemTimeUtils';
import './RedeemModal.css';

/**
 * Calculate estimated payout from redeeming tokens
 * Uses integer math to avoid precision issues
 */
function calculateEstimatedPayout(tokenBalance, finalReserve, finalSupply) {
  if (!tokenBalance || !finalReserve || !finalSupply) {
    return null;
  }

  try {
    const BigIntValue = window.BigInt;
    const balance = BigIntValue(tokenBalance);
    const reserve = BigIntValue(finalReserve);
    const supply = BigIntValue(finalSupply);

    if (supply === 0n || balance === 0n) {
      return null;
    }

    // estimatedPayout = (reserve * balance) / supply
    const payout = (reserve * balance) / supply;

    return payout.toString();
  } catch (err) {
    console.error('Error calculating payout:', err);
    return null;
  }
}

function RedeemModal({ game, tokenBalance, onClose, onConfirm, isProcessing }) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitButtonText, setSubmitButtonText] = useState('Redeem Full Balance');

  const hasBalance = tokenBalance && tokenBalance !== '0';
  const redeemEndTime = parseGameTime(game.redeem_end_time);
  const isExpired = redeemEndTime ? Date.now() >= redeemEndTime : true;

  useEffect(() => {
    // Determine if submit button should be enabled
    if (isProcessing) {
      setCanSubmit(false);
      setSubmitButtonText('Redeeming...');
      return;
    }

    if (!hasBalance) {
      setCanSubmit(false);
      setSubmitButtonText('No Tokens To Redeem');
      return;
    }

    if (isExpired) {
      setCanSubmit(false);
      setSubmitButtonText('Redeem Expired');
      return;
    }

    if (game.status_code !== 3) {
      setCanSubmit(false);
      setSubmitButtonText('Not Redeemable');
      return;
    }

    setCanSubmit(true);
    setSubmitButtonText('Redeem Full Balance');
  }, [hasBalance, isExpired, game.status_code, isProcessing]);

  const estimatedPayout = hasBalance
    ? calculateEstimatedPayout(
        tokenBalance,
        game.final_reserve_snapshot_lamports,
        game.final_supply_snapshot
      )
    : null;

  const handleSubmit = () => {
    if (canSubmit && !isProcessing) {
      onConfirm();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  return (
    <div className="redeem-modal-backdrop" onClick={handleBackdropClick}>
      <div className="redeem-modal">
        <div className="redeem-modal__header">
          <h2>Redeem Tokens</h2>
          <button
            type="button"
            className="redeem-modal__close"
            onClick={onClose}
            disabled={isProcessing}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="redeem-modal__content">
          <div className="redeem-modal__section redeem-modal__section--highlight">
            <h3>Redeem Amount</h3>
            {hasBalance ? (
              <>
                <div className="redeem-modal__amount">
                  <span className="redeem-modal__amount-label">Your Token Balance</span>
                  <span className="redeem-modal__amount-value">
                    {formatTokenAmount(tokenBalance, 6)}
                  </span>
                </div>
                <p className="redeem-modal__amount-note">
                  This redemption will use your entire token balance.
                </p>
              </>
            ) : (
              <p className="redeem-modal__no-balance">
                You do not own any of this token.
              </p>
            )}
          </div>

          <div className="redeem-modal__section">
            <h3>Game Information</h3>
            <div className="redeem-modal__info-grid">
              <div className="redeem-modal__info-item">
                <span className="redeem-modal__label">Game ID</span>
                <span className="redeem-modal__value">#{game.game_id}</span>
              </div>
              <div className="redeem-modal__info-item">
                <span className="redeem-modal__label">Status</span>
                <span className="redeem-modal__value">
                  {isExpired ? 'Expired' : 'Redeeming'}
                </span>
              </div>
              <div className="redeem-modal__info-item">
                <span className="redeem-modal__label">Redeem End Time</span>
                <span className="redeem-modal__value">
                  {formatTimestamp(redeemEndTime)}
                </span>
              </div>
            </div>
          </div>

          <div className="redeem-modal__section">
            <h3>Round Details</h3>
            <div className="redeem-modal__detail-item">
              <span className="redeem-modal__label">Mint Address</span>
              <span className="redeem-modal__value redeem-modal__value--mono">
                {game.mint}
              </span>
            </div>
            <div className="redeem-modal__detail-item">
              <span className="redeem-modal__label">Reserve Vault</span>
              <span className="redeem-modal__value redeem-modal__value--mono">
                {game.reserve_vault}
              </span>
            </div>
          </div>

          <div className="redeem-modal__section">
            <h3>Final Snapshots</h3>
            <div className="redeem-modal__info-grid">
              <div className="redeem-modal__info-item">
                <span className="redeem-modal__label">Final Reserve</span>
                <span className="redeem-modal__value">
                  {formatLamports(game.final_reserve_snapshot_lamports)} SOL
                </span>
              </div>
              <div className="redeem-modal__info-item">
                <span className="redeem-modal__label">Final Supply</span>
                <span className="redeem-modal__value">
                  {formatTokenAmount(game.final_supply_snapshot, 9)}
                </span>
              </div>
            </div>
          </div>

          {estimatedPayout && (
            <div className="redeem-modal__section redeem-modal__section--payout">
              <h3>Estimated Payout</h3>
              <div className="redeem-modal__payout">
                <span className="redeem-modal__payout-value">
                  {formatLamports(estimatedPayout)} SOL
                </span>
                <span className="redeem-modal__payout-note">
                  (Approximate based on your share of the final pool)
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="redeem-modal__footer">
          <button
            type="button"
            className="redeem-modal__button redeem-modal__button--cancel"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="redeem-modal__button redeem-modal__button--submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RedeemModal;
